/**
 * Slice LV-02 · LeviVoiceDiagPanel
 *
 * Unconditional voice diagnostic surface mounted DIRECTLY inside the
 * shared Levi drawer so the Product Owner sees it regardless of
 * whether they choose voice-intake mode, command mode, or stay in the
 * default info view. Pressing the Test buttons directly exercises
 * `window.speechSynthesis.speak()` — no backend, no router, no
 * transcription, no project/estimate/warranty context, no transcript
 * side effects.
 */
import { useEffect, useState } from "react";
import {
  stopSpeaking, primeTts, diagSnapshot, speakLeviReply, getDiag,
} from "@/lib/leviSpeech";

export default function LeviVoiceDiagPanel() {
  const [diag, setDiag] = useState(null);
  useEffect(() => {
    const tick = () => setDiag(diagSnapshot());
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, []);

  const testVoice = (lg) => {
    // Prime inside the user gesture, then route through the SHARED
    // speakLeviReply pipeline — same code path that carries every
    // normal Levi reply. Source flag "test" bypasses the voice-off
    // preference so the PO can always prove browser audio in
    // isolation.
    primeTts("test-voice-btn");
    setTimeout(() => {
      const text =
        lg === "es"
          ? "Hola, soy Levi. La voz está funcionando."
          : "Hi, I'm Levi. Voice is working.";
      speakLeviReply(text, lg, { source: "test", mode: "diag_panel" });
    }, 30);
  };

  const events = (diag?.recent || []).slice().reverse();
  const findEvt = (name) => events.find((e) => e.evt === name);
  const lastSpeak = findEvt("speak:invoked");
  const lastStart = findEvt("utterance:start");
  const lastEnd = findEvt("utterance:end");
  const lastErr = findEvt("utterance:error");
  const lastStop = events.find(
    (e) => e.evt === "stopSpeaking" || e.evt === "cancel:before-speak",
  );
  const lastNormalReq = findEvt("normalReply:speakRequested");
  const lastNormalBlock = findEvt("normalReply:blocked");
  const lastPostReplyStop = (() => {
    if (!lastNormalReq) return null;
    return events.find(
      (e) =>
        e.ts > lastNormalReq.ts &&
        (e.evt === "stopSpeaking" || e.evt === "cancel:before-speak"),
    );
  })();

  return (
    <details
      className="rounded-md border border-dashed border-orange-400 bg-orange-50/60 text-orange-900 mt-2 sticky top-0 z-10 shadow-sm"
      data-testid="levi-voice-diag"
    >
      <summary
        className="cursor-pointer select-none text-[11px] font-mono uppercase tracking-wider font-bold px-3 py-1.5 flex items-center gap-2 justify-between"
      >
        <span>LV-02 QA Diagnostics (tap to expand)</span>
        {/* PO 2026-08-06 · one always-visible action so QA can copy
            the full diagnostics after a real spoken turn without
            expanding the panel and without the panel stealing the
            mic tap-target above. */}
        <button
          type="button"
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              const parity = (typeof window !== "undefined" && window.__leviParityDiag) || null;
              const qa = (typeof window !== "undefined" && window.__leviQAState) || null;
              const snap = diagSnapshot() || {};
              const recent = getDiag(60);
              const counts = {};
              for (const ev of recent) { counts[ev.evt] = (counts[ev.evt] || 0) + 1; }
              let voices = [];
              try {
                voices = (window.speechSynthesis.getVoices() || []).map((v) => ({
                  name: v.name, lang: v.lang,
                  localService: !!v.localService, default: !!v.default,
                }));
              } catch { /* noop */ }
              const payload = {
                marker: "LeviQA-FullDiag/2026-08-06",
                ts: new Date().toISOString(),
                device_and_browser: {
                  user_agent: (navigator && navigator.userAgent) || "",
                  platform: (navigator && navigator.platform) || "",
                  languages: (navigator && navigator.languages) || [],
                  viewport: { w: window.innerWidth, h: window.innerHeight,
                              dpr: window.devicePixelRatio || 1 },
                  page_url: window.location.href,
                  build_marker: (parity && parity.marker) || "unknown",
                  family: (parity && parity.family) || null,
                  api_support: (parity && parity.api) || null,
                },
                voice: {
                  inventory_count: voices.length, inventory: voices,
                  selected_voice: (recent.slice().reverse()
                    .find((ev) => ev.evt === "speak:invoked")) || null,
                  primed: snap.primed, tts_enabled: snap.tts_enabled,
                  speaking: snap.speaking, pending: snap.pending,
                  paused: snap.paused,
                  voices_available_at_snapshot: snap.voices_available,
                  pref_raw: snap.pref_raw,
                },
                conversation_turn: {
                  session_id: qa && qa.session_id,
                  status: qa && qa.status,
                  conversation_active: qa && qa.conversation_active,
                  drawer_open: qa && qa.drawer_open,
                  tab_hidden: qa && qa.tab_hidden,
                  pending_action_type: qa && qa.pending_action,
                  last_transcript: qa && qa.last_transcript,
                  last_reply: qa && qa.last_reply,
                  turns_count: qa && qa.turns_count,
                  inactivity_notice: qa && qa.inactivity_notice,
                },
                microphone_and_stream: {
                  permission: qa && qa.permission,
                  stream: qa && qa.stream,
                  recorder: qa && qa.recorder,
                  vad_present: qa && qa.vad_present,
                },
                ui_state: {
                  drawer_open: qa && qa.drawer_open,
                  tab_hidden: qa && qa.tab_hidden,
                  status: qa && qa.status,
                },
                event_history: { count_by_event: counts, recent_60: recent },
              };
              const text = JSON.stringify(payload, null, 2);
              try {
                await navigator.clipboard.writeText(text);
                window.alert("Full Levi diagnostics copied (" + recent.length + " events)");
              } catch {
                window.prompt("Copy this JSON:", text.slice(0, 200000));
              }
            } catch (err) {
              try { window.alert("Diag copy failed: " + String(err)); } catch { /* noop */ }
            }
          }}
          className="rounded bg-indigo-600 text-white text-[10px] px-2 py-1 font-semibold normal-case"
          data-testid="levi-cmd-voice-copy-full-diag-btn"
        >
          Copy Full Diagnostics
        </button>
      </summary>
      <div className="p-3 space-y-2">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => testVoice("en")}
            className="rounded bg-orange-600 text-white text-xs px-3 py-1.5 font-semibold"
            data-testid="levi-cmd-voice-test-btn"
          >
            Test Levi Voice
          </button>
          <button
            type="button"
            onClick={() => testVoice("es")}
            className="rounded bg-orange-500 text-white text-xs px-3 py-1.5 font-semibold"
            data-testid="levi-cmd-voice-test-es-btn"
          >
            Test Spanish Voice
          </button>
          <button
            type="button"
            onClick={() => stopSpeaking("diag-stop")}
            className="rounded bg-white border border-orange-400 text-orange-800 text-xs px-3 py-1.5 font-semibold"
            data-testid="levi-cmd-voice-stop-btn"
          >
            Stop Voice
          </button>
        </div>
      {diag && (
        <div className="text-[11px] font-mono text-orange-900 space-y-1">
          <div>
            tts_enabled=<b>{String(diag.tts_enabled)}</b>{" "}
            pref_raw=<b>{JSON.stringify(diag.pref_raw)}</b>{" "}
            primed=<b>{String(diag.primed)}</b>{" "}
            voices=<b>{diag.voices_available}</b>
          </div>
          <div>
            speaking=<b>{String(diag.speaking)}</b>{" "}
            pending=<b>{String(diag.pending)}</b>{" "}
            paused=<b>{String(diag.paused)}</b>
          </div>
          <div className="space-y-0.5">
            <div>
              selected_voice=<b>{lastSpeak?.voice || "—"}</b>{" "}
              locale=<b>{lastSpeak?.lang || "—"}</b>
            </div>
            <div>
              last_speak_invoked=<b>
                {lastSpeak ? new Date(lastSpeak.ts).toLocaleTimeString() : "—"}
              </b>{" "}
              textLen=<b>{lastSpeak?.textLen ?? "—"}</b>
            </div>
            <div>
              last_onstart=<b>
                {lastStart ? new Date(lastStart.ts).toLocaleTimeString() : "—"}
              </b>
            </div>
            <div>
              last_onend=<b>
                {lastEnd ? new Date(lastEnd.ts).toLocaleTimeString() : "—"}
              </b>
            </div>
            <div className={lastErr ? "text-red-700 font-bold" : ""}>
              last_onerror_code=<b>{lastErr?.code || "—"}</b>
              {lastErr ? ` @ ${new Date(lastErr.ts).toLocaleTimeString()}` : ""}
            </div>
            <div>last_stop_reason=<b>{lastStop?.reason || "—"}</b></div>
          </div>
          <div className="mt-1 pt-1 border-t border-orange-300 space-y-0.5">
            <div className="font-bold">Normal reply pipeline:</div>
            <div>
              last_normal_reply_requested=<b>
                {lastNormalReq ? new Date(lastNormalReq.ts).toLocaleTimeString() : "—"}
              </b>{" "}
              reply_len=<b>{lastNormalReq?.reply_len ?? "—"}</b>{" "}
              mode=<b>{lastNormalReq?.mode || "—"}</b>
            </div>
            <div>
              last_normal_reply_locale=<b>{lastNormalReq?.locale || "—"}</b>{" "}
              tts_enabled_at_call=<b>{lastNormalReq ? String(lastNormalReq.tts_enabled) : "—"}</b>{" "}
              is_listening=<b>{lastNormalReq ? String(lastNormalReq.is_listening) : "—"}</b>
            </div>
            <div className={lastNormalBlock ? "text-red-700 font-bold" : ""}>
              last_normal_reply_blocked=<b>{lastNormalBlock?.reason || "—"}</b>
              {lastNormalBlock ? ` @ ${new Date(lastNormalBlock.ts).toLocaleTimeString()}` : ""}
            </div>
            <div className={lastPostReplyStop ? "text-red-700 font-bold" : ""}>
              cancel_after_normal_reply=<b>{lastPostReplyStop?.reason || lastPostReplyStop?.evt || "—"}</b>
              {lastPostReplyStop ? ` @ ${new Date(lastPostReplyStop.ts).toLocaleTimeString()}` : ""}
            </div>
          </div>
          <details className="mt-1">
            <summary className="cursor-pointer text-orange-700">
              recent events ({(diag.recent || []).length})
            </summary>
            <div className="mt-1 max-h-40 overflow-auto space-y-0.5">
              {events.map((e, i) => (
                <div key={i} className="whitespace-pre-wrap break-all">
                  {new Date(e.ts).toLocaleTimeString()} · <b>{e.evt}</b>{" "}
                  {JSON.stringify(
                    Object.fromEntries(
                      Object.entries(e).filter(([k]) => k !== "ts" && k !== "evt"),
                    ),
                  )}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
      </div>
    </details>
  );
}
